import { inject, Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';

@Injectable({
	providedIn: 'root',
})
export class SnackBarService {
	private injector = inject(Injector);
	private snackBar = inject(MatSnackBar);

	present(textKey: string, duration = 3000) {
		const translate = this.injector.get(TranslateService);
		this.snackBar.open(translate.instant(textKey), null, { duration });
	}

	presentRequestError(error: HttpErrorResponse, duration = 3000) {
		this.snackBar.open(this.handleNetworkErrors(error), null, { duration });
	}

	handleNetworkErrors = (error: HttpErrorResponse): string => {
		const translate = this.injector.get(TranslateService);

		const data = error.error;
		const status = error.status;

		// 1. network / no response from backend
		if (!data || status === 0) {
			return translate.instant('axios_errors.network');
		}

		// 2. timeout / connection aborted
		if (data.code === 'ECONNABORTED') {
			return translate.instant('axios_errors.econ', {
				support_email: environment.SUPPORT_EMAIL,
			});
		}

		// 3. server error
		if (status === 500) {
			return translate.instant('axios_errors.400_default', {
				name: data?.name,
				status,
				code: data?.code,
				msg: data?.message,
			});
		}

		// 4. bad request / unauthorized
		if (status === 400 || status === 401) {
			return translate.instant(`axios_errors.${data?.code}`);
		}

		// 5. not found
		if (status === 404) {
			if (data?.code === 6013) {
				return translate.instant(`axios_errors.${data.code}`);
			}

			return translate.instant('axios_errors.400_default', {
				name: data?.name,
				status,
				code: data?.code,
				msg: data?.message,
			});
		}

		// 6. validation / business errors (422)
		if (status === 422) {
			if (data.code === -1) {
				if (data.errors?.promo) {
					return translate.instant('axios_errors.promo');
				}

				if (data.name === 'ValidationError') {
					return translate.instant('axios_errors.6003');
				}

				return translate.instant('axios_errors.400_default', {
					name: data?.name,
					status,
					code: data?.code,
					msg: data?.message,
				});
			}

			if (data.code === 2003) {
				return translate.instant('axios_errors.2003', {
					//	time: moment(time).format('HH:mm'),
				});
			}

			if (data.code === 2023) {
				return translate.instant('axios_errors.2023', {
					support_email: environment.SUPPORT_EMAIL,
				});
			}

			if (data.code === 2004) {
				if (data.errors?.orderAt || data.errors?.orderedAt) {
					return translate.instant('axios_errors.2004_order', {
						//time: moment(time).format('HH:mm'),
					});
				}

				if (data.errors?.phone) {
					return translate.instant('axios_errors.2004_phone');
				}

				if (data.errors?.promo) {
					return translate.instant('axios_errors.2004_promo');
				}

				return translate.instant('axios_errors.2004');
			}

			return translate.instant('axios_errors.422', {
				support_email: environment.SUPPORT_EMAIL,
			});
		}

		// 7. fallback
		return translate.instant('axios_errors.400_default', {
			name: data?.name,
			status,
			code: data?.code,
			msg: data?.message,
		});
	};
}
